<template>
    <div class="lf-tabela-wrapper" ref="wrapper">
        <table class="lf-tabela">
            <thead>
                <tr>
                    <th class="lf-col-num">Nº</th>
                    <th class="lf-col-aluno">Aluno</th>
                    <th class="lf-col-input">Faltas</th>
                    <th class="lf-col-input">A.C</th>
                    <th class="lf-col-motivo">Motivo A.C</th>
                    <th class="lf-col-pct">% Freq.</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="!alunos.length">
                    <td colspan="6" class="lf-vazio">Nenhum aluno vinculado a esta turma.</td>
                </tr>
                <template v-for="{ aluno, i, anterior } in linhas">
                    <tr v-if="anterior" :key="'m' + anterior.matricula_id" class="lf-saiu">
                        <td class="lf-col-num">{{ anterior.numero || '—' }}</td>
                        <td class="lf-col-aluno">
                            {{ anterior.nome }}
                            <span class="lf-etiqueta">
                                ({{ anterior.etiqueta }}<template v-if="anterior.data_etiqueta"> em {{ formatarData(anterior.data_etiqueta) }}</template>)
                            </span>
                        </td>
                        <td colspan="4"></td>
                    </tr>
                    <tr v-if="!anterior" :key="'a' + aluno.frequencia_id" :class="{ 'lf-saiu': aluno.etiqueta }">
                        <td class="lf-col-num">{{ aluno.numero || '—' }}</td>
                        <td class="lf-col-aluno">
                            {{ aluno.nome }}
                            <span v-if="aluno.etiqueta" class="lf-etiqueta">
                                ({{ aluno.etiqueta }}<template v-if="aluno.data_etiqueta"> em {{ formatarData(aluno.data_etiqueta) }}</template>)
                            </span>
                        </td>
                        <td class="lf-col-input">
                            <integer-input v-model="aluno.faltas" :max="999" :data-row="i" data-col="faltas"
                                :disabled="somenteLeitura || aluno.editavel === false" :erro="temErro(aluno, 'faltas')"
                                @navegar="(d) => mover(i, 'faltas', d)" />
                        </td>
                        <td class="lf-col-input">
                            <integer-input v-model="aluno.aulas_compensadas" :max="999" :data-row="i" data-col="ac"
                                :disabled="somenteLeitura || aluno.editavel === false" :erro="temErro(aluno, 'aulas_compensadas')"
                                @input="(v) => aoMudarAc(aluno, v)"
                                @navegar="(d) => mover(i, 'ac', d)" />
                        </td>
                        <td class="lf-col-motivo">
                            <el-select v-model="aluno.motivo_aula_compensada_id" size="mini" clearable placeholder=""
                                :class="{ 'lf-select-erro': temErro(aluno, 'motivo_aula_compensada_id') }"
                                :disabled="somenteLeitura || aluno.editavel === false || !(Number(aluno.aulas_compensadas) > 0)">
                                <el-option v-for="m in opcoesMotivo(aluno)" :key="m.id" :label="m.nome" :value="m.id"></el-option>
                            </el-select>
                        </td>
                        <td class="lf-col-pct" :class="{ 'lf-abaixo': abaixoDoMinimo(aluno) }">{{ percentual(aluno) }}</td>
                    </tr>
                    <tr v-if="!anterior && erros[aluno.aluno_id]" :key="'e' + aluno.frequencia_id" class="lf-erro-linha">
                        <td></td>
                        <td colspan="5">{{ erros[aluno.aluno_id].mensagem }}</td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
// Grade do lançamento de frequência por período (spec_diario/frequencias/04). Enter/↓/↑ movem o foco
// para o mesmo campo do aluno seguinte/anterior.
import IntegerInput from '@/shared/integer-input'

export default {
    components: { IntegerInput },
    props: {
        alunos: { type: Array, required: true },
        // Matrículas anteriores do mesmo aluno na turma (saiu e voltou): linhas só de exibição, como no legado.
        anteriores: { type: Array, default: () => [] },
        aulasDadas: { type: [Number, String], default: '' },
        motivos: { type: Array, default: () => [] },
        erros: { type: Object, default: () => ({}) },
        somenteLeitura: { type: Boolean, default: false },
        frequenciaMinima: { type: Number, default: 75 }
    },
    computed: {
        // Alunos (com o índice real, usado na navegação por teclado) e matrículas anteriores, por nº de chamada.
        linhas () {
            const itens = this.alunos.map((aluno, i) => ({ aluno, i, anterior: null, numero: aluno.numero, nome: aluno.nome }))
            this.anteriores.forEach(a => itens.push({ aluno: null, i: -1, anterior: a, numero: a.numero, nome: a.nome }))
            return itens.sort((x, y) => {
                const nx = x.numero ? Number(x.numero) : Infinity
                const ny = y.numero ? Number(y.numero) : Infinity
                return nx === ny ? String(x.nome).localeCompare(String(y.nome)) : (nx < ny ? -1 : 1)
            })
        }
    },
    methods: {
        temErro (aluno, campo) {
            return !!(this.erros[aluno.aluno_id] && this.erros[aluno.aluno_id].campo == campo)
        },
        // % Freq. = 100 × (1 − (faltas − A.C) / aulas dadas), inteiro, como no sistema legado.
        valorPercentual (aluno) {
            const aulas = Number(this.aulasDadas)
            if (!aulas) return null
            const faltas = Number(aluno.faltas) || 0
            const ac = Number(aluno.aulas_compensadas) || 0
            const pct = Math.round(100 * (1 - (faltas - ac) / aulas))
            return Math.max(0, Math.min(100, pct))
        },
        percentual (aluno) {
            const v = this.valorPercentual(aluno)
            return v === null ? '—' : v
        },
        abaixoDoMinimo (aluno) {
            const v = this.valorPercentual(aluno)
            return v !== null && v < this.frequenciaMinima
        },
        // Motivo desativado já gravado continua aparecendo para a linha que o usa.
        opcoesMotivo (aluno) {
            if (!aluno.motivo_aula_compensada_id || this.motivos.find(m => m.id == aluno.motivo_aula_compensada_id)) {
                return this.motivos
            }
            return [...this.motivos, { id: aluno.motivo_aula_compensada_id, nome: '(motivo inativo)' }]
        },
        aoMudarAc (aluno, valor) {
            if (!(Number(valor) > 0)) {
                aluno.motivo_aula_compensada_id = null
            } else if (!aluno.motivo_aula_compensada_id && this.motivos.length == 1) {
                aluno.motivo_aula_compensada_id = this.motivos[0].id
            }
        },
        mover (linha, coluna, direcao) {
            if (!this.$refs.wrapper) return
            for (let i = linha + direcao; i >= 0 && i < this.alunos.length; i += direcao) {
                const alvo = this.$refs.wrapper.querySelector(`input[data-col="${coluna}"][data-row="${i}"]`)
                if (alvo && !alvo.disabled) {
                    alvo.focus()
                    return
                }
            }
        },
        formatarData (d) {
            return this.$moment(d).format('DD/MM/YYYY')
        }
    }
}
</script>

<style>
    .lf-tabela-wrapper {
        width: 100%;
        overflow-x: auto;
    }
    .lf-tabela {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }
    .lf-tabela thead th {
        background: #1c69b3;
        color: #fff;
        font-weight: 600;
        padding: 6px 8px;
        text-align: center;
        white-space: nowrap;
    }
    .lf-tabela tbody td {
        padding: 3px 8px;
        border-bottom: 1px solid #ebeef5;
        vertical-align: middle;
    }
    .lf-tabela tbody tr:nth-child(even) td {
        background: #fafafa;
    }
    .lf-tabela .lf-col-num {
        width: 44px;
        text-align: center;
    }
    .lf-tabela .lf-col-aluno {
        text-align: left;
        min-width: 220px;
    }
    .lf-tabela .lf-col-input,
    .lf-tabela .lf-col-pct {
        width: 80px;
        text-align: center;
    }
    .lf-tabela .lf-col-motivo {
        width: 210px;
        min-width: 160px;
    }
    .lf-tabela tr.lf-saiu td {
        background: #ececec !important;
    }
    .lf-tabela tr.lf-saiu .lf-col-aluno,
    .lf-tabela .lf-etiqueta {
        font-weight: 700;
    }
    .lf-tabela .lf-abaixo {
        color: #f56c6c;
        font-weight: 700;
    }
    .lf-tabela tr.lf-erro-linha td {
        color: #f56c6c;
        font-size: 12px;
        padding-top: 0;
        border-bottom: 1px solid #fbc4c4;
    }
    .lf-tabela .lf-select-erro .el-input__inner {
        border-color: #f56c6c !important;
    }
    .lf-tabela .lf-vazio {
        text-align: center;
        color: #909399;
        padding: 20px;
    }
</style>
